import type { OrgType, RoleCode } from '@paytms/shared';
import type { PkSelectOption } from '../ui/form/pk-select';

/**
 * 역할 6종 — 클라이언트(페이먼트코리아) 서면 확정 2026-09-07.
 *
 * `packages/shared/src/role.ts`가 서버·프론트 공통 정의이지만, 이 패키지에서 **값**을 import 하면
 * Vite dev 서버에서 CJS named export 해석에 걸린 전례가 있어(shared/index.ts 주석) 프론트는
 * 상수를 재정의한다(`shared/codes/org-type.ts`·`access-level.ts`와 같은 관례).
 * 대신 키를 `Record<RoleCode, …>`로 묶어 shared의 `RoleCode`가 바뀌면 **컴파일이 깨지게** 했다.
 *
 * ⚠ 값을 고치면 `packages/shared/src/role.ts`와 같은 값인지 확인한다 — 서버가 등록 가능 역할을
 * 403으로, 계층 불일치를 400으로 막으므로 화면이 더 넓게 열어두면 저장 단계에서만 실패한다.
 */

/** role_level 오름차순(권한이 센 순) — 선택 목록 정렬 기준 */
export const ROLE_CODES: readonly RoleCode[] = [
  'HQ_SUPER_ADMIN',
  'HQ_ADMIN',
  'DEALER_ADMIN',
  'DEALER_USER',
  'AGENT_ADMIN',
  'AGENT_USER',
];

/** 화면 표기용 역할 이름 — 서버 role.role_name 시드와 같은 문자열 */
export const ROLE_LABEL: Record<RoleCode, string> = {
  HQ_SUPER_ADMIN: '본사총괄관리자',
  HQ_ADMIN: '본사관리자',
  DEALER_ADMIN: '대리점관리자',
  DEALER_USER: '대리점사용자',
  AGENT_ADMIN: 'Agent관리자',
  AGENT_USER: 'Agent사용자',
};

/** 작을수록 상위 — 목록 정렬에 쓴다 */
export const ROLE_LEVEL: Record<RoleCode, number> = {
  HQ_SUPER_ADMIN: 1,
  HQ_ADMIN: 2,
  DEALER_ADMIN: 3,
  DEALER_USER: 4,
  AGENT_ADMIN: 5,
  AGENT_USER: 6,
};

/** 역할 × 소속 계층 — 역할 하나가 붙을 수 있는 계층은 정확히 하나다 (제조사는 계정 발급 대상이 아니다) */
export const ROLE_ORG_TYPE: Record<RoleCode, OrgType> = {
  HQ_SUPER_ADMIN: 'HQ',
  HQ_ADMIN: 'HQ',
  DEALER_ADMIN: 'DEALER',
  DEALER_USER: 'DEALER',
  AGENT_ADMIN: 'AGENT',
  AGENT_USER: 'AGENT',
};

/** 관리자 역할 — 산하 계정을 만들고 그 권한 매트릭스를 저장할 수 있는 역할 */
export const ADMIN_ROLE_CODES: readonly RoleCode[] = [
  'HQ_SUPER_ADMIN',
  'HQ_ADMIN',
  'DEALER_ADMIN',
  'AGENT_ADMIN',
];

/** 등록 가능 역할 표 — 사용자 역할(DEALER_USER·AGENT_USER)은 빈 배열이라 계정을 만들 수 없다 */
export const ROLE_GRANTABLE: Record<RoleCode, readonly RoleCode[]> = {
  HQ_SUPER_ADMIN: [
    'HQ_SUPER_ADMIN',
    'HQ_ADMIN',
    'DEALER_ADMIN',
    'DEALER_USER',
    'AGENT_ADMIN',
    'AGENT_USER',
  ],
  HQ_ADMIN: ['DEALER_ADMIN', 'DEALER_USER', 'AGENT_ADMIN', 'AGENT_USER'],
  DEALER_ADMIN: ['DEALER_USER', 'AGENT_ADMIN', 'AGENT_USER'],
  DEALER_USER: [],
  AGENT_ADMIN: ['AGENT_USER'],
  AGENT_USER: [],
};

/** 서버가 내려준 문자열이 아는 역할 코드인가 — 옛 코드(HQ_OPERATOR 등)는 false */
export function isRoleCode(value: string | null | undefined): value is RoleCode {
  // `in`은 프로토타입까지 훑어 'toString' 같은 문자열을 역할로 인정한다 — 자기 키만 본다
  return (
    value !== null &&
    value !== undefined &&
    Object.prototype.hasOwnProperty.call(ROLE_LABEL, value)
  );
}

/** 관리자 역할 여부 — 모르는 코드·미부여는 false (fail-closed) */
export function isAdminRole(value: string | null | undefined): boolean {
  return isRoleCode(value) && ADMIN_ROLE_CODES.includes(value);
}

/**
 * 본사총괄관리자 여부 — 계층 기본 권한 **밖**의 동작을 개인에게 부여할 수 있는 유일한 역할이다
 * (`packages/shared/src/permission.ts` 판정 모델, 클라이언트 대표 요청 2026-09-08).
 * 다른 관리자에게 열면 대리점관리자가 산하 계정에 자기 계층에 없는 권한을 만들어 줄 수 있다.
 * 서버 `SUPER_ADMIN_ROLE_CODE`와 같은 값이며 `grants` 저장을 403으로 막는 기준과 일치한다.
 */
export function isSuperAdminRole(value: string | null | undefined): boolean {
  return value === 'HQ_SUPER_ADMIN';
}

/** 그 역할이 부여할 수 있는 역할 — 표에 없는 코드는 빈 배열 (fail-closed) */
export function grantableRoles(value: string | null | undefined): readonly RoleCode[] {
  return isRoleCode(value) ? ROLE_GRANTABLE[value] : [];
}

/** 그 계층에 붙일 수 있는 역할 — 제조사·미선택은 빈 배열 */
export function rolesForOrgType(orgType: OrgType | null): RoleCode[] {
  if (orgType === null) {
    return [];
  }
  return ROLE_CODES.filter((code) => ROLE_ORG_TYPE[code] === orgType);
}

/**
 * 사용자관리 [역할] 셀렉트에 실제로 뜨는 목록 — 두 조건의 교집합이다.
 * ①폼에서 고른 소속 업체의 계층에 맞는 역할 ②로그인 사용자가 부여할 수 있는 역할.
 * (서버 POST/PUT /users가 각각 400·403으로 같은 규칙을 강제한다)
 */
export function assignableRoles(
  orgType: OrgType | null,
  actorRoleCode: string | null,
): RoleCode[] {
  const grantable = grantableRoles(actorRoleCode);
  return rolesForOrgType(orgType).filter((code) => grantable.includes(code));
}

/** 역할 코드 목록 → 셀렉트 옵션 (role_level 순) */
export function toRoleOptions(codes: readonly RoleCode[]): PkSelectOption<string>[] {
  return [...codes]
    .sort((a, b) => ROLE_LEVEL[a] - ROLE_LEVEL[b])
    .map((code) => ({ value: code, label: ROLE_LABEL[code] }));
}
